import {
  Coverage,
  GetCTCoverageDetailsResponseStatus,
  Resident,
  TllCoverageStatus,
} from "@graphql/api";
import { RootState } from "@redux/store";
import { PayloadAction, createSlice, isAnyOf } from "@reduxjs/toolkit";
import { coveragesApi } from "./coveragesApi";
import {
  coverageDataFormDefaultValues,
  coverageTypeFormDefaultValues,
} from "@forms";
import { mapExternalPolicyDataToCoverage } from "@utils";
import { uploadDocumentsRestApi } from "@redux/documents/documentsRestApi";
import { CoverageType } from "@enums";

import { CoverageFormDataWithTimestamps } from "./types";
import { mapExternalCoverageToExternalCoverageFormData } from "@utils/mapExternalCoverageToExternalCoverageFormData/mapExternalCoverageToExternalCoverageFormData";

export interface CoveragesState {
  coverageType: CoverageType;
  ctPolicyLocator: string;
  proofOfCoverageDetails: Coverage;
  addedCoverageDetails: {
    updatedResident: Resident;
    alreadyAddedCoverageId: string;
    cancelledTll: Coverage;
    alreadyAddedCoverageStatus: TllCoverageStatus;
    residentStatusChanged: boolean;
  };
  externalCoverageData: CoverageFormDataWithTimestamps;
  externalCoverageDocuments: File[];
  /** Policy document uploaded in the Policy Document step; addExternalCoverage attaches it by id. */
  extractionDocument: { id: string; fileName: string } | null;
  documentsUploadStatus: {
    hasDocumentsUploadFailed: boolean;
    addedCoverageId?: string;
    description?: string;
  };
  isLoading: boolean;
}

const initialState: CoveragesState = {
  coverageType: coverageTypeFormDefaultValues.coverageType,
  ctPolicyLocator: coverageTypeFormDefaultValues.policyId,
  proofOfCoverageDetails: null,
  addedCoverageDetails: {
    updatedResident: null,
    alreadyAddedCoverageId: null,
    cancelledTll: null,
    alreadyAddedCoverageStatus: null,
    residentStatusChanged: false,
  },
  externalCoverageData: {
    ...coverageDataFormDefaultValues,
  },
  externalCoverageDocuments: [],
  extractionDocument: null,
  documentsUploadStatus: {
    hasDocumentsUploadFailed: false,
    addedCoverageId: undefined,
    description: undefined,
  },
  isLoading: false,
};

const coveragesSlice = createSlice({
  name: "coveragesState",
  initialState,
  reducers: {
    setCoverageType: (state, action: PayloadAction<CoverageType>) => {
      state.coverageType = action.payload;
    },
    setCtPolicyLocator: (state, action: PayloadAction<string>) => {
      state.ctPolicyLocator = action.payload;
    },
    resetPolicyTypeForm: (state) => {
      state.coverageType = initialState.coverageType;
      state.ctPolicyLocator = initialState.ctPolicyLocator;
    },
    addExternalCoverageData: (
      state,
      action: PayloadAction<CoverageFormDataWithTimestamps>
    ) => {
      state.externalCoverageData = {
        ...action.payload,
      };
      state.proofOfCoverageDetails = mapExternalPolicyDataToCoverage(
        action.payload
      );
    },
    setExtractionDocument: (
      state,
      action: PayloadAction<CoveragesState["extractionDocument"]>
    ) => {
      state.extractionDocument = action.payload;
    },
    addCurrentExternalCoverageDataToStateForAdditionOfPolicyEvent: (
      state,
      action: PayloadAction<Coverage>
    ) => {
      state.externalCoverageData =
        mapExternalCoverageToExternalCoverageFormData(action.payload);
      state.proofOfCoverageDetails = action.payload;
    },
    saveExternalPolicyDocuments: (
      state,
      action: PayloadAction<{
        files: File[];
        isFailed?: boolean;
        coverageId?: string;
        description?: string;
      }>
    ) => {
      state.externalCoverageDocuments = action.payload.files;
      state.documentsUploadStatus.hasDocumentsUploadFailed =
        action.payload.isFailed || false;
      if (action.payload.description) {
        state.documentsUploadStatus.description = action.payload.description;
      }
      if (action.payload.coverageId) {
        state.documentsUploadStatus.addedCoverageId = action.payload.coverageId;
      }
    },
    resetExternalCoverageData: (state) => {
      state.externalCoverageData = { ...coverageDataFormDefaultValues };
      state.proofOfCoverageDetails = null;
      state.externalCoverageDocuments = [];
      state.extractionDocument = null;
      state.ctPolicyLocator = initialState.ctPolicyLocator;
      state.documentsUploadStatus = initialState.documentsUploadStatus;
      state.addedCoverageDetails = initialState.addedCoverageDetails;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        coveragesApi.endpoints.getCtCoverageDetails.matchFulfilled,
        (state, { payload }) => {
          if (
            [
              GetCTCoverageDetailsResponseStatus.Success,
              GetCTCoverageDetailsResponseStatus.AddressMismatch,
            ].includes(payload.status)
          ) {
            state.proofOfCoverageDetails = payload.coverage;
            state.addedCoverageDetails = initialState.addedCoverageDetails;
          } else {
            return { ...initialState, ctPolicyLocator: state.ctPolicyLocator };
          }
          state.isLoading = false;
        }
      )
      .addMatcher(
        isAnyOf(
          coveragesApi.endpoints.addCovertreeCoverage.matchFulfilled,
          coveragesApi.endpoints.addExternalCoverage.matchFulfilled
        ),
        (state, { payload }) => {
          state.addedCoverageDetails.cancelledTll =
            payload.cancelledCoverage ?? null;
          state.addedCoverageDetails.updatedResident = payload.resident;
          state.addedCoverageDetails.alreadyAddedCoverageId =
            payload.alreadyAddedCoverageId;
          state.addedCoverageDetails.alreadyAddedCoverageStatus =
            payload.alreadyAddedCoverageStatus;
          state.addedCoverageDetails.residentStatusChanged =
            payload.residentStatusChanged;
          if (
            [
              TllCoverageStatus.Cancelled,
              TllCoverageStatus.ScheduledCancellation,
            ].includes(payload.alreadyAddedCoverageStatus)
          ) {
            state.addedCoverageDetails.cancelledTll =
              payload.resident.coverages.find(
                (coverage) => coverage.id === payload.alreadyAddedCoverageId
              );
          }
          state.isLoading = false;
        }
      )
      .addMatcher(
        coveragesApi.endpoints.cancelTllCoverage.matchFulfilled,
        (state, { payload }) => {
          state.addedCoverageDetails.cancelledTll = payload;
          state.isLoading = false;
        }
      )
      .addMatcher(
        isAnyOf(
          coveragesApi.endpoints.getCtCoverageDetails.matchPending,
          coveragesApi.endpoints.addCovertreeCoverage.matchPending,
          coveragesApi.endpoints.addExternalCoverage.matchPending,
          coveragesApi.endpoints.cancelTllCoverage.matchPending,
          uploadDocumentsRestApi.endpoints.uploadCoverageDocuments.matchPending
        ),
        (state) => {
          state.isLoading = true;
        }
      )
      .addMatcher(
        isAnyOf(
          coveragesApi.endpoints.getCtCoverageDetails.matchRejected,
          coveragesApi.endpoints.addCovertreeCoverage.matchRejected,
          coveragesApi.endpoints.addExternalCoverage.matchRejected,
          coveragesApi.endpoints.cancelTllCoverage.matchRejected,
          uploadDocumentsRestApi.endpoints.uploadCoverageDocuments.matchRejected
        ),
        (state) => {
          state.isLoading = false;
        }
      )
      .addMatcher(
        uploadDocumentsRestApi.endpoints.uploadCoverageDocuments.matchFulfilled,
        (state) => {
          state.isLoading = false;
        }
      );
  },
});

export const selectCoverageType = (state: RootState) =>
  state.coverages.coverageType;
export const selectCtPolicyLocator = (state: RootState) =>
  state.coverages.ctPolicyLocator;
export const selectProofOfCoverageDetails = (state: RootState) =>
  state.coverages.proofOfCoverageDetails;
export const selectAddedCoverageDetails = (state: RootState) =>
  state.coverages.addedCoverageDetails;
export const selectExternalCoverageData = (state: RootState) =>
  state.coverages.externalCoverageData;
export const selectExternalPolicyDocuments = (state: RootState) =>
  state.coverages.externalCoverageDocuments;
export const selectHasDocumentsUploadFailed = (state: RootState) =>
  state.coverages.documentsUploadStatus.hasDocumentsUploadFailed;
export const selectDocumentsUploadStatus = (state: RootState) =>
  state.coverages.documentsUploadStatus;
export const selectIsLoading = (state: RootState) => state.coverages.isLoading;
export const selectExtractionDocument = (state: RootState) =>
  state.coverages.extractionDocument;
export const selectCoveragesState = (state: RootState) => state.coverages;

export const {
  addCurrentExternalCoverageDataToStateForAdditionOfPolicyEvent,
  addExternalCoverageData,
  resetExternalCoverageData,
  setExtractionDocument,
  saveExternalPolicyDocuments,
  setCoverageType,
  resetPolicyTypeForm,
  setCtPolicyLocator,
} = coveragesSlice.actions;
export default coveragesSlice;
